import { contactListSorts, formatDate, toBerlinDateKey, type ContactListSort } from '@brimo/shared';

/**
 * Sort and entry date range of the contacts list (Nico feedback FB-003, D-320…D-322): the persisted sort, the
 * header toggle of „Eingang“ and the presets of the „Zeitraum“ filter. Pure functions except the storage access,
 * which tolerates a missing or blocked `localStorage` (private mode) by falling back to „Name“.
 */

export const SORT_STORAGE_KEY = 'brimo.contacts.sort';

export const SORT_OPTIONS: { value: ContactListSort; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'newest', label: 'Neueste' },
  { value: 'oldest', label: 'Älteste' },
  { value: 'next_step', label: 'Nächster Schritt' },
  { value: 'financing', label: 'Finanzierbar' },
];

function isSort(value: string | null): value is ContactListSort {
  return value !== null && (contactListSorts as readonly string[]).includes(value);
}

/** The sort the user chose last; „Name“ when nothing (valid) is stored. */
export function readStoredSort(): ContactListSort {
  try {
    const value = window.localStorage.getItem(SORT_STORAGE_KEY);
    return isSort(value) ? value : 'name';
  } catch {
    return 'name';
  }
}

export function storeSort(sort: ContactListSort): void {
  try {
    if (sort === 'name') window.localStorage.removeItem(SORT_STORAGE_KEY);
    else window.localStorage.setItem(SORT_STORAGE_KEY, sort);
  } catch {
    // Storage is optional: the sort stays for this visit only.
  }
}

/** Header click on „Eingang“: newest first, a second click oldest first, then newest again. */
export function toggleEntrySort(sort: ContactListSort): ContactListSort {
  return sort === 'newest' ? 'oldest' : 'newest';
}

export interface EntryRange {
  from: string;
  until: string;
}

export const entryRangePresets = ['today', 'last7', 'last30', 'quarter'] as const;
export type EntryRangePreset = (typeof entryRangePresets)[number];

export const entryRangePresetLabels: Record<EntryRangePreset, string> = {
  today: 'Heute',
  last7: '7 Tage',
  last30: '30 Tage',
  quarter: 'Dieses Quartal',
};

/** YYYY-MM-DD plus whole days (calendar arithmetic, independent of the local time zone). */
export function addDays(day: string, days: number): string {
  const [year, month, date] = day.split('-').map(Number);
  const value = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (date ?? 1) + days));
  return value.toISOString().slice(0, 10);
}

/** The days of a preset relative to today in Berlin; every preset ends today. */
export function presetRange(preset: EntryRangePreset, now: Date = new Date()): EntryRange {
  const today = toBerlinDateKey(now);
  switch (preset) {
    case 'today':
      return { from: today, until: today };
    case 'last7':
      return { from: addDays(today, -6), until: today };
    case 'last30':
      return { from: addDays(today, -29), until: today };
    case 'quarter': {
      const [year = '1970', month = '01'] = today.split('-');
      const quarterMonth = Math.floor((Number(month) - 1) / 3) * 3 + 1;
      return { from: `${year}-${String(quarterMonth).padStart(2, '0')}-01`, until: today };
    }
  }
}

/** The preset a range equals today, if any. */
export function matchingPreset(range: EntryRange, now: Date = new Date()): EntryRangePreset | null {
  for (const preset of entryRangePresets) {
    const candidate = presetRange(preset, now);
    if (candidate.from === range.from && candidate.until === range.until) return preset;
  }
  return null;
}

/** Label of the „Zeitraum“ chip: „Zeitraum“, „Zeitraum: 7 Tage“, „Zeitraum: 01.10.2026 – 15.10.2026“, „ab …“, „bis …“. */
export function entryRangeLabel(range: EntryRange, now: Date = new Date()): string {
  if (range.from === '' && range.until === '') return 'Zeitraum';
  const preset = matchingPreset(range, now);
  if (preset !== null) return `Zeitraum: ${entryRangePresetLabels[preset]}`;
  const day = (key: string) => formatDate(`${key}T12:00:00Z`);
  if (range.from !== '' && range.until !== '') return `Zeitraum: ${day(range.from)} – ${day(range.until)}`;
  if (range.from !== '') return `Zeitraum: ab ${day(range.from)}`;
  return `Zeitraum: bis ${day(range.until)}`;
}
